/**
 * Компонент системного сообщения в чате Activity.
 * Используется для служебных событий, которые не являются
 * пользовательскими комментариями.
 * Если у события есть entity_type и entity_uuid — элемент кликабелен
 * и открывает карточку сущности.
 */
import { useCallback, useMemo } from 'react';
import { Center, Text, UnstyledButton } from '@mantine/core';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import { useActivityScopeActions } from '../../hooks/useActivityScopeActions';
import { formatFeedListDate } from '../utils/ActivityList.utils';

type ActivityChatSystemItemProps = {
  item: ActivityChatItem;
};

/**
 * Рендерит системное сообщение в чате активности.
 */
export function ActivityChatSystemItem({ item }: ActivityChatSystemItemProps) {
  const { onScopeClick, isScopeClickable } = useActivityScopeActions();

  const entityScope = useMemo(() => {
    if (!item.entity_type || !item.entity_uuid) return null;
    return {
      entity_type: item.entity_type,
      entity_uuid: item.entity_uuid,
    };
  }, [item.entity_type, item.entity_uuid]);

  const clickable = isScopeClickable(entityScope);

  const handleClick = useCallback(() => {
    if (clickable && entityScope) {
      onScopeClick(entityScope);
    }
  }, [clickable, entityScope, onScopeClick]);

  const content = (
    <Text
      size='xs'
      c='white'
      style={{
        backgroundColor: '#0077bc59',
        padding: '2px 7px',
        borderRadius: 16,
        backdropFilter: 'blur(8px)',
        cursor: clickable ? 'pointer' : undefined,
      }}
    >
      {item.author_name}
      {' • '}
      {item.content}
      {' • '}
      {formatFeedListDate(item.created_at)}
    </Text>
  );

  if (clickable) {
    return (
      <Center key={item.id} my={12}>
        <UnstyledButton onClick={handleClick}>{content}</UnstyledButton>
      </Center>
    );
  }

  return (
    <Center key={item.id} my={12}>
      {content}
    </Center>
  );
}
